import { Controller } from "@hotwired/stimulus"

// The Turnstile widget's lifecycle (docs/03, T16b).
//
// Cloudflare's own auto-render finds `.cf-turnstile` once, when its script
// loads. That is enough for a page that is rendered once and never touched.
// This form is neither: it lives on step 4 of a wizard that starts hidden, it
// re-renders whole after a booth conflict, and it can sit open for an hour
// while somebody chases a logo file. So the widget is rendered EXPLICITLY, by
// a controller that connects and disconnects with the element.
//
// Three failures this exists to prevent, all of them silent:
//
//   **A token that expired while the form was open.** Turnstile tokens last
//   about five minutes. Without the expiry callback the submit fails the
//   check and the person is told to "complete the check again" for something
//   they already did. `reset()` gets a fresh one before they ever notice.
//
//   **A re-render with no widget.** After a booth conflict the form comes
//   back with every field kept — and, under auto-render, no widget at all,
//   because Cloudflare's script already ran. The second submit then cannot
//   pass a check it was never offered.
//
//   **A loader that never arrives.** Blocked, offline, or an ad blocker. The
//   page says so rather than presenting a submit button that always fails.
export default class extends Controller {
  static targets = ["widget", "message"]
  static values = { siteKey: String, theme: { type: String, default: "auto" } }

  connect() {
    this.onReady = this.render.bind(this)
    // Cloudflare's loader calls `varaTurnstileReady`, which dispatches this.
    // Listening as well as checking covers both orders: script first, or
    // controller first.
    document.addEventListener("turnstile:ready", this.onReady)
    if (window.turnstile) this.render()
  }

  disconnect() {
    document.removeEventListener("turnstile:ready", this.onReady)
    this.remove()
  }

  render() {
    if (!window.turnstile || this.widgetId !== undefined) return

    this.widgetId = window.turnstile.render(this.widgetTarget, {
      sitekey: this.siteKeyValue,
      theme: this.themeValue,
      "expired-callback": () => this.refresh(),
      // A token is single-use and a timeout is not the person's fault; both
      // are fixed by asking for another one rather than by telling them off.
      "timeout-callback": () => this.refresh(),
      "error-callback": () => this.say("We couldn't verify you. Please try again in a moment.")
    })

    this.say("")
  }

  refresh() {
    if (this.widgetId === undefined) return

    window.turnstile.reset(this.widgetId)
  }

  remove() {
    if (this.widgetId === undefined) return

    window.turnstile.remove(this.widgetId)
    this.widgetId = undefined
  }

  say(message) {
    if (!this.hasMessageTarget) return

    this.messageTarget.textContent = message
    this.messageTarget.hidden = message === ""
  }
}
